/* global axios */
import ApiClient from './ApiClient';

class KanbanAPI extends ApiClient {
  constructor() {
    super('kanban', { accountScoped: true });
  }

  // Funnels
  getFunnels(params = {}) {
    return axios.get(`${this.url}/funnels`, { params });
  }

  getFunnel(funnelId) {
    return axios.get(`${this.url}/funnels/${funnelId}`);
  }

  createFunnel(data) {
    const payload = { kanban_funnel: data };
    return axios.post(`${this.url}/funnels`, payload);
  }

  updateFunnel(funnelId, data) {
    const payload = { kanban_funnel: data };
    return axios.patch(`${this.url}/funnels/${funnelId}`, payload);
  }

  deleteFunnel(funnelId) {
    return axios.delete(`${this.url}/funnels/${funnelId}`);
  }

  // Stages
  getStages(funnelId) {
    return axios.get(`${this.url}/funnels/${funnelId}/stages`);
  }

  createStage(funnelId, data) {
    const payload = { kanban_stage: data };
    return axios.post(`${this.url}/funnels/${funnelId}/stages`, payload);
  }

  updateStage(funnelId, stageId, data) {
    const payload = { kanban_stage: data };
    return axios.patch(`${this.url}/funnels/${funnelId}/stages/${stageId}`, payload);
  }

  deleteStage(funnelId, stageId) {
    return axios.delete(`${this.url}/funnels/${funnelId}/stages/${stageId}`);
  }

  reorderStages(funnelId, stageIds) {
    return axios.patch(`${this.url}/funnels/${funnelId}/stages/reorder`, { stages: stageIds });
  }

  // Conversations
  getStageConversations(stageId, params = {}) {
    return axios.get(`${this.url}/stages/${stageId}/conversations`, { params });
  }

  // Mock data for development/demo purposes
  getMockConversations(stageId, params = {}) {
    const mockConversations = {
      1: [
        {
          id: 101,
          account_id: 1,
          display_id: 12345,
          status: 'open',
          stage_id: 1,
          meta: {
            sender: {
              name: 'João Silva',
              email: 'joao@example.com',
              avatar: null
            },
            assignee: {
              name: 'Ana Costa',
              id: 1
            }
          },
          messages: [
            {
              content: 'Olá! Tenho interesse no seu produto. Podem me passar mais informações?',
              created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2h ago
              message_type: 'incoming'
            }
          ],
          created_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), // 3h ago
          updated_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2h ago
          unread_count: 1,
          priority: 'medium',
          labels: ['lead', 'produto']
        },
        {
          id: 102,
          account_id: 1,
          display_id: 12346,
          status: 'open',
          stage_id: 1, 
          meta: {
            sender: {
              name: 'Maria Santos',
              email: 'maria@company.com',
              avatar: null
            },
            assignee: {
              name: 'Carlos Mendes',
              id: 2
            }
          },
          messages: [
            {
              content: 'Estou com dúvidas sobre o preço do plano enterprise.',
              created_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), // 4h ago
              message_type: 'incoming'
            }
          ],
          created_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(), // 5h ago
          updated_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), // 4h ago
          unread_count: 0,
          priority: 'high',
          labels: ['pricing', 'enterprise']
        }
      ],
      2: [
        {
          id: 201,
          account_id: 1,
          display_id: 12347,
          status: 'open',
          stage_id: 2,
          meta: {
            sender: {
              name: 'Pedro Oliveira',
              email: 'pedro@startup.com',
              avatar: null
            },
            assignee: {
              name: 'Ana Costa',
              id: 1
            }
          },
          messages: [
            {
              content: 'Gostaria de agendar uma demo do produto para nossa equipe.',
              created_at: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), // 1h ago
              message_type: 'incoming'
            }
          ],
          created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2h ago
          updated_at: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), // 1h ago
          unread_count: 1,
          priority: 'medium',
          labels: ['demo', 'agendamento']
        }
      ],
      3: [
        {
          id: 301,
          account_id: 1,
          display_id: 12348,
          status: 'open',
          stage_id: 3,
          meta: {
            sender: {
              name: 'Lucas Tech',
              email: 'lucas@techcorp.com',
              avatar: null
            },
            assignee: {
              name: 'Roberto Silva',
              id: 3
            }
          },
          messages: [
            {
              content: 'Já testamos o produto e queremos fechar o contrato. Qual o próximo passo?',
              created_at: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30min ago
              message_type: 'incoming'
            }
          ],
          created_at: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(), // 6h ago
          updated_at: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30min ago
          unread_count: 1,
          priority: 'high',
          labels: ['fechamento', 'contrato']
        },
        {
          id: 302,
          account_id: 1,
          display_id: 12349,
          status: 'open',
          stage_id: 3,
          meta: {
            sender: {
              name: 'Fernanda Reis',
              email: 'fernanda@business.com',
              avatar: null
            },
            assignee: {
              name: 'Ana Costa', 
              id: 1
            }
          },
          messages: [
            {
              content: 'Preciso de algumas informações técnicas para aprovar internamente.',
              created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2h ago
              message_type: 'incoming'
            }
          ],
          created_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), // 4h ago
          updated_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2h ago
          unread_count: 0,
          priority: 'medium',
          labels: ['técnico', 'aprovação']
        }
      ]
    };

    const conversations = mockConversations[stageId] || [];
    
    // Simulate API response format
    return Promise.resolve({
      data: {
        payload: conversations,
        meta: {
          count: conversations.length,
          current_page: 1,
          total_pages: 1
        }
      }
    });
  }

  getConversationKanban(conversationId) {
    return axios.get(`${this.baseUrl()}/conversations/${conversationId}/kanban`);
  }

  moveConversation(conversationId, stageId, reason = null) {
    return axios.patch(`${this.baseUrl()}/conversations/${conversationId}/kanban/move`, {
      stage_id: stageId,
      reason
    });
  }

  createConversationNote(conversationId, content, createdByName = null) {
    return axios.post(`${this.baseUrl()}/conversations/${conversationId}/kanban/notes`, {
      content,
      created_by_name: createdByName
    });
  }

  bulkMoveConversations(conversationIds, stageId, reason = null) {
    return axios.patch(`${this.baseUrl()}/conversations/kanban/bulk_move`, {
      conversation_ids: conversationIds,
      stage_id: stageId,
      reason
    });
  }
}

export default new KanbanAPI();
